import React, { useEffect, useRef, useState } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import cx from 'classnames';
import { useDispatch, useSelector } from "react-redux";
import { change, formValueSelector } from "redux-form";
import { FormattedMessage, injectIntl } from "react-intl";
import Button from "react-bootstrap/Button";
import Flatpickr from "react-flatpickr";
import confirmDatePlugin from "flatpickr/dist/plugins/confirmDate/confirmDate";
import moment from "moment-timezone";
import { closeHeaderModal, openHeaderModal } from "../../actions/siteadmin/modalActions";
import { createBookingAction } from "../../actions/booking/createBookingAction";
import { readBookingDraft, writeBookingDraft } from "../../helpers/bookingDraft";
import { flatPickerLocale } from "../../helpers/flatPickerLocale";
import messages from "../../locale/messages";
import bookingIcon from '/public/dashboard/bookingIcon.svg';
import scheduleIcon from '/public/dashboard/scheduleIcon.svg';
import promoArrowIcon from '/public/dashboard/promoArrowIcon.svg';
import defaultDateAndTimeIcon from '/public/Icons/defaultDateAndTimeIcon.svg';
import n from "flatpickr/dist/themes/material_green.css";
import confirmDateCss from "flatpickr/dist/plugins/confirmDate/confirmDate.css";
import r from '../../styles/flatPickerCalendar.css';
import bt from '../../components/commonStyle.css'
import s from './BookingForm.css'

// "How would you like to book?" — this is where the TWO booking flows live, exactly like the app's
// when_do_you_need sheet. Each is a SEPARATE flow gated by its own admin toggle, with NO combined
// "choose your provider" sub-screen:
//   - "Browse Preferred Pros"  (preferredProviderSelection) -> the client picks a worker from the list.
//   - "Choose for me"          (randomProviderSelection)    -> broadcast live to all nearby workers
//                                                              (ring -> 1-min board fallback).
// Picking a flow first collects the appointment date/time, then goes STRAIGHT into that flow.
const BookingForm = ({ formData, shouldOpenSafetyMeasure, intl: { formatMessage, locale } }) => {

    const dispatch = useDispatch()
    const orderId = formData?.bookingData?.id
    const [pickTime, setPickTime] = useState(false)
    const [apptDate, setApptDate] = useState(null)
    const [flow, setFlow] = useState(null) // 'preferred' | 'choose'
    const flatPickerRef = useRef(null)

    // Resumable booking: restore the chosen flow + appointment date from the persisted draft when the
    // "How would you like to book?" sheet reopens (after a refresh / Back / post-login return).
    useEffect(() => {
        const draft = readBookingDraft(orderId)
        if (draft.flow === 'preferred' || draft.flow === 'choose') {
            setFlow(draft.flow)
            if (draft.scheduleDate) setApptDate(new Date(draft.scheduleDate))
            setPickTime(true)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Mirror the chosen flow + appointment date as the customer picks them.
    useEffect(() => {
        if (!orderId) return
        writeBookingDraft(orderId, {
            flow: flow || undefined,
            scheduleDate: apptDate ? new Date(apptDate).getTime() : undefined,
        })
    }, [orderId, flow, apptDate])

    // Admin Booking-flow toggles — each gates ITS flow (different flows, no sub-flow).
    const isPreferredProviderEnable = useSelector(state => state?.siteSettings?.data?.preferredProviderSelection)
    const isRandomProviderEnable = useSelector(state => state?.siteSettings?.data?.randomProviderSelection)
    // Carried into the Choose-for-me booking mutation (same fields the provider screens use).
    const promoId = useSelector(state => formValueSelector('PromoCodeForm')(state, 'promoId'))
    const isPromoIdRemove = useSelector(state => formValueSelector('PromoCodeForm')(state, 'isPromoIdRemove'))
    const paymentMethodId = useSelector(state => formValueSelector('PaymentMethod')(state, 'paymentMethodId'))
    const description = useSelector(state => formValueSelector('OrderDetails')(state, 'description'))
    const estimatedHours = useSelector(state => formValueSelector('OrderDetails')(state, 'estimatedHours'))
    const questionnaire = useSelector(state => formValueSelector('OrderDetails')(state, 'questionnaire'))

    // Step 1: pick a flow -> reveal the appointment-time picker (defaulted to now).
    const chooseFlow = (which) => {
        setFlow(which)
        setApptDate(new Date())
        setPickTime(true)
    }

    // Quick date/time preset chips (port of the app's view_cart slot chips). One tap sets a real
    // appointment datetime (apptDate -> scheduledAt via resolveScheduledAt); the calendar below stays
    // for custom picks. Mirrors _dayAt(daysAhead, hour): today + N days at a fixed local hour.
    const dayAt = (daysAhead, hour) => {
        const n = new Date()
        return new Date(n.getFullYear(), n.getMonth(), n.getDate() + daysAhead, hour, 0, 0, 0)
    }
    const slotChips = [
        { label: messages.slotTomorrowAm, when: () => dayAt(1, 9) },
        { label: messages.slotTomorrowPm, when: () => dayAt(1, 13) },
        { label: messages.slotTomorrowEve, when: () => dayAt(1, 18) },
        { label: messages.slotInTwoDays, when: () => dayAt(2, 9) },
    ]

    const resolveScheduledAt = () => {
        if (apptDate) {
            const m = moment(apptDate)
            if (m.isAfter(moment().add(1, 'minute'))) return m.unix()
        }
        return null
    }

    // Step 2: confirm the time, then go STRAIGHT into the chosen flow (no provider-method picker).
    const handleContinue = async () => {
        const scheduledAt = resolveScheduledAt()
        dispatch(change('OrderDetails', 'scheduledAt', scheduledAt))
        dispatch(closeHeaderModal('bookingForm'))

        if (flow === 'preferred') {
            // Browse Preferred Pros: open the provider list so the client picks a worker.
            dispatch(openHeaderModal('preferredProviderForm'))
            return
        }

        // Choose for me: broadcast live to ALL eligible workers (dispatchMode 'auto'), then show the
        // waiting screen (ring -> board fallback -> opens the job). Same logic the old provider-selection
        // "Choose for me" card used. A worker shared via their /pro link is treated as a preferred booking.
        const bookingData = formData?.bookingData;
        const orderItem = bookingData?.orderItemsList?.[0];
        const validPromoId = !isPromoIdRemove ? (promoId ?? bookingData?.promoId) : null;
        let sharedPro = null;
        try { if (typeof window !== 'undefined') sharedPro = window.localStorage.getItem('bh_preferred_pro'); } catch (e) {}
        // LOCKED (booking flow): Choose-for-me is a BROADCAST → open the waiting screen with
        // isPreffered=false (no 3rd arg) so the 60s board timer arms. The MAIN preferred flow never comes
        // through here (it goes BookingForm -> preferredProviderForm -> NearestProviderForm w/ isPreffered=true).
        // Known caveat: a /pro shared-pro booking IS preferred yet enters here as isPreffered=false — it
        // would board after 60s. Edge case only; flagged to Paul (docs/web-booking-flow.md §0), left as-is.
        dispatch(openHeaderModal('nearestProviderForm'))
        await dispatch(createBookingAction({
            orderId: bookingData?.id,
            requestCurrency: bookingData?.currency,
            reviewDescription: description?.trim() ? description : null,
            dropOffLocation: orderItem?.dropLocation,
            dropOffLat: orderItem?.dropLat,
            dropOffLng: orderItem?.dropLng,
            promoId: validPromoId,
            paymentType: Number(paymentMethodId),
            preferredPartnerId: sharedPro || undefined,
            dispatchMode: sharedPro ? undefined : 'auto',
            scheduledAt: scheduledAt || null,
            estimatedHours: estimatedHours || null,
            questionnaire: questionnaire || null,
        }))
        try { if (typeof window !== 'undefined') window.localStorage.removeItem('bh_preferred_pro'); } catch (e) {}
    }

    const handleGoBack = () => {
        if (pickTime) { setPickTime(false); setFlow(null); return; }
        dispatch(closeHeaderModal('bookingForm'))
        shouldOpenSafetyMeasure && dispatch(openHeaderModal('safetyMeasureForm'))
    }
    const openCalendar = () => { if (flatPickerRef.current) flatPickerRef.current.open() }

    if (pickTime) {
        return (
            <>
                <div className={cx(bt.commonLabelText, bt.space2)}>{formatMessage(messages.dateAndTime)}</div>
                {/* One-tap presets (app parity). They set a real scheduledAt; the calendar below stays
                    for custom picks. Date/time is still always required — these only speed it up. */}
                <div className={s.slotChipRow}>
                    {slotChips.map((chip, i) => {
                        const target = chip.when()
                        const isActive = apptDate && moment(apptDate).isSame(moment(target), 'minute')
                        return (
                            <button
                                type="button"
                                key={i}
                                onClick={() => setApptDate(target)}
                                className={cx(s.slotChip, isActive && s.slotChipActive)}
                            >
                                {formatMessage(chip.label)}
                            </button>
                        )
                    })}
                </div>
                <div className='scheduleBookingDateTimeContainer'>
                    <Flatpickr
                        id="book-now-date-picker"
                        data-enable-time
                        options={{
                            enableTime: true,
                            monthSelectorType: "static",
                            disableMobile: 'true',
                            time_24hr: false,
                            static: true,
                            minDate: new Date(),
                            maxDate: moment().add(31, 'day').toDate(),
                            dateFormat: 'Y-m-d, h:i K',
                            // The picker used to close on the first date click — the client lost the
                            // calendar before setting the time ("ça saute"). Keep it open until the
                            // client confirms: closeOnSelect off + a confirmDate "OK" button that closes
                            // it (and commits the choice via onClose) only when they're done.
                            closeOnSelect: false,
                            plugins: [confirmDatePlugin({ confirmText: 'OK', showAlways: true, theme: 'light' })],
                            locale: locale ? flatPickerLocale[locale] : flatPickerLocale['en-US']
                        }}
                        onClose={(dates) => dates && dates[0] && setApptDate(dates[0])}
                        value={apptDate ? new Date(apptDate) : new Date()}
                        onReady={(_, __, fp) => { flatPickerRef.current = fp }}
                    />
                    <Button onClick={openCalendar} aria-label={formatMessage(messages.chooseDateTime)} className={cx(s.scheduleClockBtn, 'scheduleClockBtnRtl')}>
                        <img src={defaultDateAndTimeIcon} alt="" />
                    </Button>
                </div>
                {apptDate && <span className={cx(bt.commonSize14, bt.fontWeight400, bt.primaryColor)}>
                    {moment(apptDate).locale(locale).format(`ddd, MMM D, [${formatMessage(messages.at)}] h:mma`)}
                </span>}
                <div className={cx('buttonGroupContainer', 'buttonGroupDivider')}>
                    <Button onClick={handleGoBack} className={bt.btnSecondary}><FormattedMessage {...messages.goBackText} /></Button>
                    <Button onClick={handleContinue} className={bt.btnPrimary}><FormattedMessage {...messages.confirmBooking} /></Button>
                </div>
            </>
        )
    }

    return (
        <>
            <div className={s.needFormCardContainer}>
                {/* The two flows — each shown only if its admin toggle is ON, fully separate. */}
                {isPreferredProviderEnable == '1' && <div onClick={() => chooseFlow('preferred')} className={cx(s.needFormCardFlex, 'svgImg', bt.cursorPointer)}>
                    <div className={s.needFormCardImgContainer}>
                        <img src={bookingIcon} alt="Browse Preferred Pros" />
                        <span className={cx(bt.commonSize16, bt.fontWeight600, bt.primaryColor)}><FormattedMessage {...messages.preferredProviderLabel} /></span>
                    </div>
                    <img src={promoArrowIcon} alt="promoArrowIcon" className={'iconRotate'} />
                </div>}
                {isRandomProviderEnable == '1' && <div onClick={() => chooseFlow('choose')} className={cx(s.needFormCardFlex, 'svgImg', bt.cursorPointer)}>
                    <div className={s.needFormCardImgContainer}>
                        <img src={scheduleIcon} alt="Choose for me" />
                        <span className={cx(bt.commonSize16, bt.fontWeight600, bt.primaryColor)}><FormattedMessage {...messages.radomProviderLabel} /></span>
                    </div>
                    <img src={promoArrowIcon} alt="promoArrowIcon" className={'iconRotate'} />
                </div>}
            </div>
            <div className={cx('buttonGroupContainer', 'buttonGroupDivider')}>
                <Button onClick={() => handleGoBack()} className={bt.btnSecondary}><FormattedMessage {...messages.goBackText} /> </Button>
            </div>
        </>
    )
}

export default injectIntl(withStyles(n, confirmDateCss, r, bt, s)(BookingForm));
